import { useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardS from '~/components/ProductCardV3/variants/ProductCardS/ProductCardS';
import {
  ProductListAnalyticsProvider,
  useCategoryBlockProductList,
} from '~/components/ProductListAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { createCategoryUrl } from './shared/utils';

interface ProductsCarouselSliderSuperPricesProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
}

const containerClass = 'mx-auto w-full max-w-(--layout-max-width)';
const slideClass =
  'basis-[88px]! shrink-0 grow-0 pl-0! [&:not(:last-child)]:mr-4 [&>div]:w-full md:basis-[calc(1/6*100%-16px+calc(16px/6))]!';
const itemClass = 'w-[88px] shrink-0 md:w-[227px]';
const carouselContentClass = 'pb-2 -mx-4 px-4 w-[calc(100%+32px)]!';
const skeletonContainerClass =
  'flex gap-4 [&>*]:w-[88px] [&>*]:shrink-0 md:[&>*]:w-[227px]';

export const ProductsCarouselSliderSuperPrices: React.FC<
  ProductsCarouselSliderSuperPricesProps
> = ({ category, className, ...props }) => {
  const { pathname } = useLocation();
  const { isMobile } = useGlobal();
  const blockList = useCategoryBlockProductList(category, 'carousel');

  // Hide the block when there are no products and none are on the way —
  // home-page categories get their products with the categories tree, so
  // productsRequest may never run and never become fulfilled.
  if (!category.products.length && !category.productsRequest.isLoading) {
    return null;
  }

  const categoryId = category?.subcategory[0]?.id;

  if (!category.subcategory.length || !categoryId) {
    return null;
  }

  const linkTo =
    pathname !== '/search' ? createCategoryUrl({ id: categoryId }) : undefined;

  const products = category.products.slice(0, isMobile ? 10 : 15);
  const items = products.map((product, index) => ({
    id: product.id,
    node: <ProductCardS product={product} index={index} className="w-full!" />,
  }));

  const skeletonComponent = (
    <div className={skeletonContainerClass}>
      {Array.from({ length: 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="s" />
      ))}
    </div>
  );

  const commonProps = {
    title: category.name,
    linkTo,
    categoryId,
    items,
    skeletonComponent: items.length === 0 ? skeletonComponent : undefined,
    headerImage: category.previewImage,
  };

  return (
    <ProductListAnalyticsProvider list={blockList}>
      <div {...props} className={cn(containerClass, className)}>
        {isMobile ? (
          <ProductCarouselMobile
            {...commonProps}
            showViewAllOnMobile
            itemClassName={itemClass}
          />
        ) : (
          <ProductCarouselDesktop
            {...commonProps}
            navigationVariant="header"
            slideClassName={slideClass}
            contentClassName={carouselContentClass}
          />
        )}
      </div>
    </ProductListAnalyticsProvider>
  );
};
